import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useLocation } from "react-router-dom";
import io, { Socket } from "socket.io-client";
import { useBoardAndItems, useSlug, useTokenHook, useUser } from "@hooks/context";
import { useItemPage } from "@hooks/api";
import { useSharedColumnsTourModal, useWorkspaceList } from "@hooks/ui";
import useBoardName from "@hooks/api/useBoardName";
import { ADD_ITEM_PROGRESS_CHANNEL, addNewItem } from "@components/addItem/AddItemUtils";
import { MondayApiQueryInstance } from "@services/monday/monday-service-ts";
import { generateBoardUrl, generateItemUrl } from "@utils/mondayLinks";
import logger from "../../../../lib/logger";
import { initialSyncWizardState, syncWizardReducer } from "./syncWizard.reducer";
import { SyncWizardActions, SyncWizardData } from "./syncWizard.types";
import { BACK_ACTION, getSyncWizardBackAction } from "./syncWizardBackAction";
import { filterAvailableWizardItems, parseExcludedItemIds } from "./syncWizardItems";

const ITEMS_PER_PAGE = 25;
const MIN_LOADING_DURATION_MS = 1800;
const SUCCESS_SETTLE_DELAY_MS = 350;
const ADD_ITEM_SOCKET_TIMEOUT_MS = 120000;
const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL || "http://localhost:8095";
const SEARCH_DEBOUNCE_MS = 300;
const TOTAL_WIZARD_STEPS = 4;
const ITEM_ID_RADIX = 10;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type AddItemSocketMessage = {
  message?: string;
  action?: string;
  userId?: string;
  targetGroupId?: string;
  targetBoardId?: string;
  createdTargetItemIds?: string[];
};

const getSubmitErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "We couldn't share these items right now. Please try again.";
};

const broadcastProgressMessage = (
  type: "add-item-completed" | "add-item-failed",
  createdTargetItemIds: string[] = [],
) => {
  try {
    const channel = new BroadcastChannel(ADD_ITEM_PROGRESS_CHANNEL);
    channel.postMessage({ type, createdTargetItemIds });
    channel.close();
  } catch {
    // BroadcastChannel not supported
  }
};

const stepTitles = ["Select items to share", "Choose destination", "Review columns", "Confirm"];

export const useSyncWizardController = () => {
  const location = useLocation();
  const { currentBoard } = useBoardAndItems();
  const { slug } = useSlug();
  const { userId, accountId } = useUser();
  const { token } = useTokenHook();
  const [state, dispatch] = useReducer(syncWizardReducer, initialSyncWizardState);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const loadingProgressRef = useRef(state.loadingProgress);

  const {
    pages,
    isLoading: isItemsLoading,
    isValidating,
    hasMore,
    pageCount,
    setSize,
    error: itemsError,
    mutate: mutateItems,
  } = useItemPage(currentBoard, { limit: ITEMS_PER_PAGE, searchTerm: state.debouncedSearchTerm });
  const {
    workspaceListdata,
    isLoading: isWorkspacesLoading,
    error: workspacesError,
    mutate: mutateWorkspaces,
  } = useWorkspaceList(true);
  const {
    sharedColumnsTour: sharedColumns,
    unmatchedColumnsTour: unmatchedColumns,
    isLoading: isSharedColumnsTourLoading,
    isError: hasSharedColumnsError,
    mutate: mutateSharedColumns,
  } = useSharedColumnsTourModal(
    [currentBoard ?? 0, parseInt(state.targetBoard?.id ?? "0", ITEM_ID_RADIX) || 0],
    true,
  );
  const { boardName } = useBoardName(currentBoard ?? undefined);

  const excludedItemIds = useMemo(
    () => parseExcludedItemIds(location.search),
    [location.search],
  );

  const visibleItems = useMemo(
    () => filterAvailableWizardItems(pages?.[state.currentPage]?.items ?? [], excludedItemIds),
    [pages, state.currentPage, excludedItemIds],
  );

  const isPageLoading = isItemsLoading || (isValidating && visibleItems.length === 0);

  const clearProgressInterval = useCallback(() => {
    if (progressRef.current) {
      clearInterval(progressRef.current);
      progressRef.current = null;
    }
  }, []);

  const startProgressAnimation = useCallback(() => {
    clearProgressInterval();
    dispatch({ type: "SET_LOADING_PROGRESS", payload: 8 });
    loadingProgressRef.current = 8;
    progressRef.current = setInterval(() => {
      const nextProgress = Math.min(
        loadingProgressRef.current + Math.max(1, (95 - loadingProgressRef.current) * 0.15),
        95,
      );
      loadingProgressRef.current = nextProgress;
      dispatch({
        type: "SET_LOADING_PROGRESS",
        payload: nextProgress,
      });
    }, 120);
  }, [clearProgressInterval]);

  const waitForAddItemCompletion = useCallback(
    (expectedBoardId: string, expectedGroupId: string): Promise<string[]> =>
      new Promise((resolve, reject) => {
        const socket = socketRef.current;
        if (!socket) {
          resolve([]);
          return;
        }

        const matchesCurrentSubmission = (data: AddItemSocketMessage): boolean =>
          data.action === "add_item" &&
          String(data.userId) === String(userId) &&
          String(data.targetBoardId) === String(expectedBoardId) &&
          String(data.targetGroupId) === String(expectedGroupId);

        const cleanUp = (timeoutId: ReturnType<typeof setTimeout>) => {
          clearTimeout(timeoutId);
          socket.off("success", onSuccess);
          socket.off("error", onError);
        };

        const onSuccess = (data: AddItemSocketMessage) => {
          if (!matchesCurrentSubmission(data)) return;
          cleanUp(timeoutId);
          resolve((data.createdTargetItemIds ?? []).map((id) => String(id)));
        };

        const onError = (data: AddItemSocketMessage) => {
          if (!matchesCurrentSubmission(data)) return;
          cleanUp(timeoutId);
          reject(new Error(data.message ?? "Add item failed"));
        };

        const timeoutId = setTimeout(() => {
          cleanUp(timeoutId);
          reject(new Error("Timed out waiting for add-item completion"));
        }, ADD_ITEM_SOCKET_TIMEOUT_MS);

        socket.on("success", onSuccess);
        socket.on("error", onError);
      }),
    [userId],
  );

  const goToPrevPage = useCallback(() => {
    dispatch({ type: "SET_CURRENT_PAGE", payload: Math.max(0, state.currentPage - 1) });
  }, [state.currentPage]);

  const goToNextPage = useCallback(() => {
    if (state.currentPage + 1 < pageCount) {
      dispatch({ type: "SET_CURRENT_PAGE", payload: state.currentPage + 1 });
      return;
    }

    if (hasMore) {
      void setSize((size) => size + 1);
      dispatch({ type: "SET_CURRENT_PAGE", payload: state.currentPage + 1 });
    }
  }, [state.currentPage, pageCount, hasMore, setSize]);

  const handleRetryItems = useCallback(() => {
    void mutateItems();
  }, [mutateItems]);

  const handleRetryWorkspaces = useCallback(() => {
    void mutateWorkspaces();
  }, [mutateWorkspaces]);

  const handleRetrySharedColumns = useCallback(() => {
    void mutateSharedColumns();
  }, [mutateSharedColumns]);

  const canNext =
    (state.step === 1 && state.selectedItems.length > 0) ||
    (state.step === 2 &&
      state.selectedWorkspace !== null &&
      state.targetBoard !== null &&
      state.targetGroup !== null) ||
    state.step >= 3;

  const handlePrimaryAction = useCallback(async () => {
    if (state.step < TOTAL_WIZARD_STEPS) {
      dispatch({ type: "SET_SUBMIT_ERROR_MESSAGE", payload: null });
      dispatch({ type: "SET_STEP", payload: (state.step + 1) as 1 | 2 | 3 | 4 });
      return;
    }

    if (
      !currentBoard ||
      !userId ||
      !accountId ||
      !token ||
      !state.targetBoard ||
      !state.targetGroup
    ) {
      return;
    }

    const itemIds = state.selectedItems
      .map((id) => parseInt(id, ITEM_ID_RADIX))
      .filter((id) => !Number.isNaN(id));

    if (itemIds.length === 0) {
      return;
    }

    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    dispatch({ type: "SET_SUBMIT_ERROR_MESSAGE", payload: null });
    dispatch({ type: "RESET_CREATED_TARGET_ITEM_IDS" });
    startProgressAnimation();
    const submitStartedAt = Date.now();

    try {
      await addNewItem({
        targetGroupId: state.targetGroup.id,
        targetBoardId: state.targetBoard.id,
        userId: String(userId),
        accountId: String(accountId),
        currentBoard: String(currentBoard),
        itemIds,
        token: String(token),
        processSubitems: state.processSubitems,
        processUpdates: state.processUpdates,
        removeItemDisabled: () => undefined,
        removeItemLoading: () => undefined,
        showStartToast: false,
      });

      const syncedTargetItemIds = await waitForAddItemCompletion(
        state.targetBoard.id,
        state.targetGroup.id,
      );
      dispatch({ type: "SET_CREATED_TARGET_ITEM_IDS", payload: syncedTargetItemIds });

      const elapsedMs = Date.now() - submitStartedAt;
      if (elapsedMs < MIN_LOADING_DURATION_MS) {
        await delay(MIN_LOADING_DURATION_MS - elapsedMs);
      }

      clearProgressInterval();
      dispatch({ type: "SET_LOADING_PROGRESS", payload: 100 });
      broadcastProgressMessage("add-item-completed", syncedTargetItemIds);
      await delay(SUCCESS_SETTLE_DELAY_MS);
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
      dispatch({ type: "SET_CONFIRMED", payload: true });
    } catch (error) {
      logger.error("Failed to submit sync wizard payload", { error });
      clearProgressInterval();
      broadcastProgressMessage("add-item-failed");
      dispatch({ type: "SET_LOADING_PROGRESS", payload: 0 });
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
      dispatch({ type: "SET_SUBMIT_ERROR_MESSAGE", payload: getSubmitErrorMessage(error) });
    }
  }, [
    state.step,
    state.targetBoard,
    state.targetGroup,
    state.selectedItems,
    state.processSubitems,
    state.processUpdates,
    currentBoard,
    userId,
    accountId,
    token,
    waitForAddItemCompletion,
    startProgressAnimation,
    clearProgressInterval,
  ]);

  const handleBack = useCallback(() => {
    const backAction = getSyncWizardBackAction(state.step);

    if (backAction === BACK_ACTION.PREVIOUS_STEP) {
      dispatch({ type: "GO_TO_PREVIOUS_STEP" });
      return;
    }

    void MondayApiQueryInstance.closeAppFeatureModal();
  }, [state.step]);

  const handleDone = useCallback(async () => {
    try {
      if (state.targetBoard?.id && slug) {
        const hasSingleCreatedItem = state.createdTargetItemIds.length === 1;
        if (!hasSingleCreatedItem && state.createdTargetItemIds.length === 0) {
          logger.info("No created target item IDs found, falling back to board URL", {
            targetBoardId: state.targetBoard.id,
          });
        }
        const url = hasSingleCreatedItem
          ? generateItemUrl(slug, state.targetBoard.id, state.createdTargetItemIds[0])
          : generateBoardUrl(slug, state.targetBoard.id);
        window.open(url, "_blank", "noopener,noreferrer");
      }
      await MondayApiQueryInstance.closeAppFeatureModal();
    } catch (error) {
      logger.error("Failed to navigate to resynchronization page", { error });
    }
  }, [state.targetBoard, state.createdTargetItemIds, slug]);

  useEffect(() => {
    if (!userId) return;

    const socketConnection = io(`${SOCKET_BASE_URL}/?userid=${userId}&accountId=${accountId}`);
    socketRef.current = socketConnection;

    return () => {
      socketConnection.disconnect();
      if (socketRef.current === socketConnection) {
        socketRef.current = null;
      }
    };
  }, [userId, accountId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch({ type: "SET_DEBOUNCED_SEARCH_TERM", payload: state.searchTerm });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [state.searchTerm]);

  useEffect(() => {
    dispatch({ type: "SET_CURRENT_PAGE", payload: 0 });
    void setSize(1);
  }, [state.debouncedSearchTerm, setSize]);

  useEffect(
    () => () => {
      clearProgressInterval();
    },
    [clearProgressInterval],
  );

  useEffect(() => {
    loadingProgressRef.current = state.loadingProgress;
  }, [state.loadingProgress]);

  const data: SyncWizardData = useMemo(
    () => ({
      currentBoard: currentBoard ?? 0,
      boardName,
      visibleItems,
      isPageLoading,
      pageCount,
      hasMore,
      hasItemsError: !!itemsError,
      hasWorkspaceError: !!workspacesError,
      isWorkspacesLoading,
      workspaceListdata: workspaceListdata ?? [],
      sharedColumns: sharedColumns ?? [],
      unmatchedColumns: unmatchedColumns ?? [],
      isSharedColumnsTourLoading,
      hasSharedColumnsError,
    }),
    [
      currentBoard,
      boardName,
      visibleItems,
      isPageLoading,
      pageCount,
      hasMore,
      itemsError,
      workspacesError,
      isWorkspacesLoading,
      workspaceListdata,
      sharedColumns,
      unmatchedColumns,
      isSharedColumnsTourLoading,
      hasSharedColumnsError,
    ],
  );

  const actions: SyncWizardActions = useMemo(
    () => ({
      setSelectedItems: (ids: string[]) =>
        dispatch({
          type: "SET_SELECTED_ITEMS",
          payload: ids,
        }),
      setSearchTerm: (value: string) =>
        dispatch({
          type: "SET_SEARCH_TERM",
          payload: value,
        }),
      goToPrevPage,
      goToNextPage,
      setSelectedWorkspace: (workspaceId: string | null) =>
        dispatch({
          type: "SET_SELECTED_WORKSPACE",
          payload: workspaceId,
        }),
      setTargetBoard: (targetBoard) =>
        dispatch({
          type: "SET_TARGET_BOARD",
          payload: targetBoard,
        }),
      setTargetGroup: (targetGroup) =>
        dispatch({
          type: "SET_TARGET_GROUP",
          payload: targetGroup,
        }),
      setProcessSubitems: (value: boolean) =>
        dispatch({
          type: "SET_PROCESS_SUBITEMS",
          payload: value,
        }),
      setProcessUpdates: (value: boolean) =>
        dispatch({
          type: "SET_PROCESS_UPDATES",
          payload: value,
        }),
      handleRetryItems,
      handleRetryWorkspaces,
      handleRetrySharedColumns,
      handleBack,
      handlePrimaryAction,
      handleDone,
    }),
    [
      goToPrevPage,
      goToNextPage,
      handleRetryItems,
      handleRetryWorkspaces,
      handleRetrySharedColumns,
      handleBack,
      handlePrimaryAction,
      handleDone,
    ],
  );

  return {
    state,
    data,
    actions,
    canNext,
    stepTitles,
  };
};
